import axios from "axios";
import config from "../../config/environment";

export const CREDENTIALS_BASE =
  config.BACKEND_URL || "https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production";

const hasContext = (v) => v && String(v) !== "null" && String(v) !== "undefined";

export const getCredentialId = (credential) => {
  if (!credential) return "";
  const id = credential._id?.$oid ?? credential._id;
  return id != null ? String(id) : "";
};

/**
 * Load credentials for an app from all relevant docdb query shapes, merging tenant-wide
 * and user-scoped connections (deduped by credential id).
 */
export const fetchAllCredentialsForApp = async ({ tenant, user, appSlug, appLabel }) => {
  const appValues = [...new Set([appLabel, appSlug].filter(Boolean))];
  const queryUrls = [];

  for (const appValue of appValues) {
    if (hasContext(tenant)) {
      queryUrls.push(
        `${CREDENTIALS_BASE}/docdb/credentials?tenant=${encodeURIComponent(tenant)}&app=${encodeURIComponent(appValue)}`
      );
      queryUrls.push(
        `${CREDENTIALS_BASE}/docdb/credentials?tenant=${encodeURIComponent(tenant)}&appSlug=${encodeURIComponent(appValue)}`
      );
    }
    if (hasContext(user)) {
      queryUrls.push(
        `${CREDENTIALS_BASE}/docdb/credentials?user=${encodeURIComponent(user)}&app=${encodeURIComponent(appValue)}`
      );
      queryUrls.push(
        `${CREDENTIALS_BASE}/docdb/credentials?user=${encodeURIComponent(user)}&appSlug=${encodeURIComponent(appValue)}`
      );
    }
  }

  if (queryUrls.length === 0) return [];

  const seen = new Set();
  const merged = [];

  const results = await Promise.all(
    queryUrls.map(async (url) => {
      try {
        const res = await axios.get(url, { timeout: 10000 });
        return Array.isArray(res.data) ? res.data : [];
      } catch {
        return [];
      }
    })
  );

  for (const list of results) {
    for (const cred of list) {
      const id = getCredentialId(cred);
      if (!id || seen.has(id)) continue;
      seen.add(id);
      merged.push(cred);
    }
  }

  merged.sort((a, b) => (Number(b.lastUsed) || 0) - (Number(a.lastUsed) || 0));
  return merged;
};

/** Pick best credential from list: lastUsed if any, else last in list. */
export const pickBestCredential = (credentials) => {
  if (!credentials || credentials.length === 0) return null;
  const withTimestamp = credentials.filter((c) => c && c.lastUsed != null);
  if (withTimestamp.length > 0) {
    return withTimestamp.reduce((prev, cur) => (prev.lastUsed > cur.lastUsed ? prev : cur));
  }
  return credentials[credentials.length - 1];
};
